import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PlugZap } from 'lucide-react';
import { get } from '../../nucleo/api';
import { useSinal, useTempoRealConectado } from '../../nucleo/tempo-real';
import type { EstadoCanais } from './tipos';

/**
 * Fase 2 (W3, F1a): o sinal `conversas` do SSE (mensagem recebida, enviada, transferida, encerrada) recarrega caixa, lista
 * e painel na hora. Sem SSE, o polling volta com o intervalo antigo (`ms`). Devolve o `refetchInterval` da consulta.
 */
export function useIntervaloConversas(ms: number): number | false {
  const qc = useQueryClient();
  useSinal('conversas', () => void qc.invalidateQueries({ queryKey: ['conversas'] }));
  return useTempoRealConectado() ? false : ms;
}

/** Conectores de canal conectados e módulos opcionais disponíveis (Pipeline, Agenda). */
export function useEstadoCanais() {
  return useQuery({ queryKey: ['conversas', 'estado'], queryFn: () => get<EstadoCanais>('/conversas/estado'), staleTime: 30_000 });
}

export function useDebounce<T>(valor: T, ms = 300) {
  const [d, setD] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setD(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return d;
}

/** Aviso de função desativada com o link para a tela de conectores (M3.5). */
export function AvisoConector({ texto }: { texto: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-laranja bg-sup-1 px-4 py-3 text-sm">
      <PlugZap className="size-4 shrink-0 text-laranja" aria-hidden />
      <p className="min-w-0 flex-1 basis-64 text-tinta-2">
        {texto} {t('comum.conectarEm')}
      </p>
      <Link to="/integracoes/integracoes" className="-my-0.5 py-0.5 font-medium text-acento-forte hover:underline">
        {t('comum.irParaIntegracoes')}
      </Link>
    </div>
  );
}
